import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Chip, IconButton, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { ProductRecognitionCropScanResponse } from '../../../types';
import { ProductRecognitionViewer } from './ProductRecognitionViewer';

const getClassificationColor = (classificationType: string): string => {
  switch (classificationType) {
    case 'product':
      return '#4CAF50'; // Green for products
    case 'carton':
      return '#FF9800'; // Orange for cartons
    default:
      return '#2196F3'; // Blue for unknown/other types
  }
};

interface CropScanItemProps {
  cropScan: ProductRecognitionCropScanResponse & {
    isPending?: boolean;
    shelfColumnLabel?: string;
  };
  index: number;
  imageUrl: string;
  onCropScanClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onCropViewerClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onDelete?: (productCropScanId: number) => void;
  isSelected?: boolean;
}

export const CropScanItem: React.FC<CropScanItemProps> = ({
  cropScan,
  index,
  imageUrl,
  onCropScanClick,
  onCropViewerClick,
  onDelete,
  isSelected,
}) => {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 1,
        cursor: onCropScanClick ? 'pointer' : 'default',
        backgroundColor: isSelected ? '#FFF3CD' : '#FFFFFF',
        border: isSelected ? '2px solid #FFC107' : '2px solid transparent',
        '&:hover': onCropScanClick
          ? {
              backgroundColor: isSelected ? '#FFE69C' : 'action.hover',
            }
          : {},
      }}
      onClick={() => onCropScanClick?.(cropScan)}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Crop Image */}
        <Box
          sx={{
            minWidth: '60px',
            minHeight: '60px',
            alignSelf: 'flex-start',
          }}
        >
          <ProductRecognitionViewer
            imageUrl={imageUrl}
            cropScans={[cropScan]}
            mode="crop"
            focusCropScan={cropScan}
            width={60}
            height={60}
            onClick={() => onCropViewerClick?.(cropScan)}
            hidePolygons
          />
        </Box>

        {/* Crop Info */}
        <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ fontSize: '12px' }}>
              # {index + 1}
            </Typography>
            <Chip
              label={cropScan.classification_type}
              size="small"
              sx={{
                backgroundColor: getClassificationColor(cropScan.classification_type),
                color: 'white',
                fontWeight: 500,
                textTransform: 'capitalize',
                fontSize: '12px',
              }}
            />
            {cropScan.shelfColumnLabel && (
              <Chip
                label={cropScan.shelfColumnLabel}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  borderColor: '#1976d2',
                  color: '#1976d2',
                }}
              />
            )}
            {cropScan.isPending && (
              <Chip label="Pending" color="warning" size="small" sx={{ fontSize: '12px' }} />
            )}
            {!cropScan.isPending && cropScan.barcode && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Complete"
                color="success"
                size="small"
                sx={{ fontSize: '12px' }}
              />
            )}
          </Stack>

          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '12px',
                textAlign: 'left',
                wordBreak: 'break-all',
              }}
            >
              {cropScan.barcode
                ? `${cropScan.barcode} • ${cropScan.product_name}${
                    cropScan.product_id ? ` • Product ID: ${cropScan.product_id}` : ''
                  }`
                : '\u00A0'}
            </Typography>
          </Box>
        </Stack>

        {/* Delete Button */}
        {cropScan.barcode && cropScan.isPending && onDelete && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(cropScan.product_crop_scan_id);
            }}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.light',
                color: 'error.contrastText',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
};
